import { Icon } from "@/components/ui/icon";
import { Skeleton } from "@/components/ui/skeleton";
import type { DashboardStats } from "@/types";

interface StatCellProps {
  label: string;
  value: number;
  icon: string;
  sub?: string;
  subPositive?: boolean;
  subDanger?: boolean;
  isLoading: boolean;
  progress?: number;
  className?: string;
}

function StatCell({ label, value, icon, sub, subPositive, subDanger, isLoading, progress, className = "" }: StatCellProps) {
  return (
    <div className={`flex flex-col gap-2 p-5 bg-card ${className}`} style={{ minHeight: "140px" }}>
      <div className="flex items-start justify-between">
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
        <div className="rounded border border-border p-1.5">
          <Icon name={icon} size={14} className="text-muted-foreground" />
        </div>
      </div>
      {isLoading ? (
        <Skeleton className="h-9 w-16 mt-1" />
      ) : (
        <p
          className="font-mono text-4xl font-bold tracking-tight leading-none mt-1"
          style={{ color: "hsl(var(--foreground))" }}
        >
          {value ?? 0}
        </p>
      )}
      {!isLoading && sub && (
        <div className="mt-auto">
          {progress !== undefined ? (
            <div className="space-y-1">
              <p className="font-sans text-xs text-muted-foreground">{sub}</p>
              <div className="h-1 w-full rounded-full bg-secondary overflow-hidden">
                <div
                  className="h-full rounded-full bg-foreground transition-all"
                  style={{ width: `${Math.min(progress * 100, 100)}%` }}
                />
              </div>
            </div>
          ) : (
            <p className={`flex items-center gap-1 font-sans text-xs ${
              subDanger ? "text-red-400" : subPositive ? "text-emerald-400" : "text-muted-foreground"
            }`}>
              {subPositive && <Icon name="trending-up-01" size={11} />}
              {subDanger && <Icon name="alert-01" size={11} />}
              {sub}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

interface DashboardStatsProps {
  stats: DashboardStats | null;
  isLoading: boolean;
}

function weekOverWeek(stats: DashboardStats | null): { text: string; positive?: boolean } {
  if (!stats) return { text: "" };
  const now = stats.totalAlerts;
  const prev = stats.previousWeekAlerts ?? 0;
  if (prev === 0) return { text: now === 0 ? "No changes detected yet" : "vs none last week" };
  const pct = Math.round(((now - prev) / prev) * 100);
  if (pct === 0) return { text: "Same as last week" };
  return { text: `${pct > 0 ? "+" : ""}${pct}% vs last week`, positive: pct > 0 };
}

export function DashboardStatsCard({ stats, isLoading }: DashboardStatsProps) {
  const wow = weekOverWeek(stats);
  const limit = stats?.competitorLimit ?? null;

  return (
    // No overflow-hidden — use per-cell border radius instead to avoid clipping numbers
    <div className="grid grid-cols-3 border border-border rounded-lg">
      <StatCell
        label="Alerts this week"
        value={stats?.totalAlerts ?? 0}
        icon="notification-01"
        sub={wow.text}
        subPositive={wow.positive}
        isLoading={isLoading}
        className="rounded-tl-lg rounded-bl-lg border-r border-border"
      />
      <StatCell
        label="Competitors tracked"
        value={stats?.competitors ?? 0}
        icon="radar-01"
        sub={limit ? `of ${limit} domains used` : "Unlimited on your plan"}
        isLoading={isLoading}
        progress={stats && limit ? stats.competitors / limit : undefined}
        className="border-r border-border"
      />
      <StatCell
        label="High-signal alerts"
        value={stats?.signalBreakdown?.find(s => s.score === "high")?.count ?? 0}
        icon="alert-01"
        sub="Requires review"
        subDanger
        isLoading={isLoading}
        className="rounded-tr-lg rounded-br-lg"
      />
    </div>
  );
}
